import React, { useCallback, useRef, useState } from 'react';
import { Receipt, Plus, X } from 'lucide-react';
import { Holding } from '../../types';
import { useTaxPlan } from '../../hooks/useTaxPlan';
import { TaxPlanSummary } from './TaxPlanSummary';
import { TaxPlanEstimateRow } from './TaxPlanEstimateRow';
import { HarvestOpportunities } from './HarvestOpportunities';
import { TaxHarvestEstimator } from './TaxHarvestEstimator';

// Fallback marginal rate when the user has no saved estimates to infer one from.
// Matches the Tax Harvest Estimator's default bracket ($45,001–$120,000, 32.5%).
const DEFAULT_BRACKET_RATE = 0.325;

export function TaxPlan({ holdings }: { holdings: Holding[] }) {
  const { estimates, deleteEstimate, getSummary } = useTaxPlan();
  const summary = getSummary();
  const hasEstimates = estimates.length > 0;

  // "Add estimate" panel — hosts the shared CGT estimator (with a coin picker).
  // Saving flows through the estimator's existing addEstimate() path, which this
  // component re-reads via useTaxPlan's cross-instance sync, so the list updates.
  const [adding, setAdding] = useState(false);
  const [presetCoinId, setPresetCoinId] = useState<string | undefined>(undefined);
  const addPanelRef = useRef<HTMLDivElement>(null);

  const scrollToPanel = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        addPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, []);

  const openAdd = useCallback((coinId?: string) => {
    setPresetCoinId(coinId);
    setAdding(true);
    scrollToPanel();
  }, [scrollToPanel]);

  // Use the most recently saved estimate's bracket to estimate harvest savings.
  const bracketRate = hasEstimates
    ? estimates[estimates.length - 1].taxBracketRate
    : DEFAULT_BRACKET_RATE;

  return (
    <section id="tax-plan" className="scroll-mt-24">
      {/* Header + Add estimate */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p
            className="uppercase mb-1.5"
            style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: 'var(--color-bronze)' }}
          >
            Tax Plan
          </p>
          <h2 className="text-slate-100" style={{ fontSize: 19, fontWeight: 500 }}>
            Your planned CGT for the year
          </h2>
          <p className="text-slate-400 mt-1.5" style={{ fontSize: 13 }}>
            Saved tax estimates across all your coins. Numbers stay frozen at the values when you saved them.
          </p>
        </div>

        {!adding && (
          <button
            type="button"
            onClick={() => openAdd()}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-lg transition-colors bg-[rgba(212,180,131,0.1)] hover:bg-[rgba(212,180,131,0.18)] border border-[rgba(212,180,131,0.32)] text-[#b89968] flex-shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Add estimate
          </button>
        )}
      </div>

      {/* Add estimate panel — the shared estimator, hosted with a coin picker */}
      {adding && (
        <div
          ref={addPanelRef}
          className="mt-5 rounded-2xl bg-slate-800/40 border border-slate-700 p-6 space-y-6"
          style={{ scrollMarginTop: 24 }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p
                className="uppercase mb-1"
                style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: 'var(--color-bronze)' }}
              >
                New estimate
              </p>
              <h3 className="text-slate-100" style={{ fontSize: 16, fontWeight: 500 }}>
                Estimate CGT on a planned sale
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setAdding(false)}
              aria-label="Close"
              className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <TaxHarvestEstimator hosted initialCoinId={presetCoinId} />
        </div>
      )}

      {!hasEstimates ? (
        /* Empty state — the header + Add estimate button above remain the entry point */
        <div className="mt-5 flex items-center gap-3 p-5 rounded-xl bg-[rgb(var(--overlay)/0.02)] border border-slate-700/50">
          <Receipt className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <p className="text-[13px] text-slate-500">
            No saved estimates yet. Use “Add estimate” to pick a coin and save your first scenario.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Annual summary */}
          <TaxPlanSummary summary={summary} />

          {/* Saved estimates list */}
          <div className="space-y-2">
            {estimates.map((e) => (
              <TaxPlanEstimateRow key={e.id} estimate={e} onDelete={deleteEstimate} />
            ))}
          </div>

          {/* Harvest opportunities */}
          <div className="pt-6" style={{ borderTop: '0.5px solid rgb(var(--overlay) / 0.06)' }}>
            <HarvestOpportunities holdings={holdings} bracketRate={bracketRate} onAddToPlan={openAdd} />
          </div>
        </div>
      )}
    </section>
  );
}
